import { useMemo, useState } from "react";
import type { PnlRuleCmsSettings } from "@cede/types";
import { useSnapshotTimestamps } from "./useSnapshotTimestamps";
import { TIME_RANGE_OPTIONS, type TimeRangeKey } from "@/components/features/leaderboards/pnl/utils";
import { isEpochEnded as isEpochEndedHelper } from "@/utils/pnl";
import type { HydratedUserSnapshots } from "@/utils/pnl/pnl-chart";

interface UsePnlChartTimeRangeArgs {
  epoch?: { id: number; start: Date; end: Date; rule: PnlRuleCmsSettings };
  users: HydratedUserSnapshots[];
  defaultTimeRange?: TimeRangeKey;
}

/**
 * Time-range selector state for the PnL evolution chart.
 *
 * The range list is filtered against the same epoch + users the chart plots.
 */
export function usePnlChartTimeRange({ epoch, users, defaultTimeRange = "12h" }: UsePnlChartTimeRangeArgs) {
  const [timeRange, setTimeRange] = useState<TimeRangeKey>(defaultTimeRange);
  const { latestSnapshotTimestamp } = useSnapshotTimestamps(users);

  // Mirror `PnlEvolutionChart`'s `effectiveNow` derivation so range filtering
  // matches the chart's x-domain math exactly.
  const isEpochEnded = isEpochEndedHelper(epoch);
  const effectiveNow =
    latestSnapshotTimestamp > 0
      ? latestSnapshotTimestamp
      : epoch
        ? Math.min(Date.now(), epoch.end.getTime())
        : Date.now();

  const availableTimeRanges = useMemo(() => {
    if (!epoch) return [];
    const elapsed = effectiveNow - epoch.start.getTime();
    return TIME_RANGE_OPTIONS.filter((opt) => opt.durationMs === null || elapsed > opt.durationMs);
  }, [epoch, effectiveNow]);

  const effectiveTimeRange = useMemo(() => {
    if (availableTimeRanges.some((opt) => opt.key === timeRange)) {
      return timeRange;
    }
    return availableTimeRanges[availableTimeRanges.length - 1]?.key ?? TIME_RANGE_OPTIONS[0].key;
  }, [availableTimeRanges, timeRange]);

  const activeRangeIndex = useMemo(
    () =>
      Math.max(
        0,
        availableTimeRanges.findIndex((opt) => opt.key === effectiveTimeRange),
      ),
    [availableTimeRanges, effectiveTimeRange],
  );

  return { timeRange: effectiveTimeRange, setTimeRange, availableTimeRanges, activeRangeIndex, isEpochEnded };
}
